import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userService } from '../../services/userService';
import { apiClient } from '../../services/apiClient';
import { useAuthStore } from '../../store/authStore';
import { useDealStore } from '../../store/dealStore';
import { SurveyorProfile, Rating, SURVEY_CATEGORIES, RequestState } from '../../types';
import { getSurveyorRank } from '../../utils/surveyorRank';
import { toNumber } from '../../utils/number';
import { useTheme } from '../../theme';
import { AnimatedIcon } from '../../components/icons';

export const SurveyorPublicProfile: React.FC = () => {
  const { surveyorId } = useParams<{ surveyorId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { deals, fetchDeals } = useDealStore();
  const { isDark } = useTheme();

  const [profile, setProfile] = useState<SurveyorProfile | null>(null);
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [reviewStars, setReviewStars] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [hoverStar, setHoverStar] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!surveyorId) return;
    Promise.all([
      userService.getSurveyorProfile(surveyorId),
      apiClient.get(`/surveyors/${surveyorId}/ratings/`).then((r: any) => r.data?.results ?? r.data ?? []).catch(() => [] as Rating[]),
      fetchDeals(),
    ]).then(([prof, rts]) => {
      setProfile(prof as SurveyorProfile);
      setRatings(rts as Rating[]);
    }).catch((e: any) => setError(e?.message || 'Failed to load profile'))
      .finally(() => setIsLoading(false));
  }, [surveyorId]);

  const closedDealsWithSurveyor = deals.filter(
    (d) => d.surveyor?.id === surveyorId && (d.status === RequestState.CLOSED || d.status === RequestState.CONFIRMED),
  );
  const alreadyReviewed = ratings.some((r) => r.from_user.id === user?.id);
  const canReview = closedDealsWithSurveyor.length > 0 && !alreadyReviewed && !submitted;

  const handleSubmitReview = async () => {
    if (!reviewStars || !surveyorId) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const dealId = closedDealsWithSurveyor[closedDealsWithSurveyor.length - 1]?.id;
      await apiClient.post(`/surveyors/${surveyorId}/ratings/`, {
        stars: reviewStars,
        comment: reviewComment.trim() || undefined,
        deal_id: dealId,
      });
      const fresh = await apiClient.get(`/surveyors/${surveyorId}/ratings/`);
      setRatings((fresh as any).data?.results ?? (fresh as any).data ?? []);
      setSubmitted(true);
      setReviewStars(0);
      setReviewComment('');
    } catch (e: any) {
      setSubmitError(e?.response?.data?.detail || e?.message || 'Could not submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return (
    <div style={styles.center}>
      <div style={styles.spinner} />
      <p style={{ color: 'var(--sc-text-secondary)' }}>Loading profile details...</p>
    </div>
  );
  
  if (error || !profile) return (
    <div style={styles.center}>
      <div style={{ fontSize: 44, marginBottom: 12 }}>⚠️</div>
      <p style={{ color: 'var(--sc-error-text)' }}>{error || 'Surveyor profile not found'}</p>
      <button onClick={() => navigate(-1)} style={styles.backBtn} className="sc-press">← Go Back</button>
    </div>
  );

  const avgRating = toNumber(profile.rating_average);
  const totalJobs = toNumber(profile.total_jobs);
  const rank = getSurveyorRank(avgRating, totalJobs);
  const isVerified = profile.verification_status === 'APPROVED';
  const fullStars = Math.floor(avgRating);
  const ratingPct = (avgRating / 5) * 100;

  const myCategories = SURVEY_CATEGORIES.filter((cat) =>
    cat.services.some((s) => profile.service_types.includes(s)),
  );

  const starLabels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

  return (
    <div style={styles.pageContainer}>
      <button onClick={() => navigate(-1)} style={styles.backBtn} className="sc-press">← Back</button>

      {/* Hero Header Card */}
      <div style={styles.heroCard} className="sc-animate-scale-in">
        <div style={styles.heroLeft}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={styles.avatar}>
              {(profile.first_name?.[0] || '?').toUpperCase()}
              {(profile.last_name?.[0] || '').toUpperCase()}
            </div>
            {isVerified && (
              <div style={styles.verifiedBadgeOverlay} title="Verified Certificate">✓</div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={styles.nameRow}>
              <h1 style={styles.name}>{profile.first_name} {profile.last_name}</h1>
              {isVerified && (
                <span style={styles.verifiedTag}>Verified</span>
              )}
              <span style={{ ...styles.rankTag, background: rank.bg, color: rank.color }}>
                {rank.icon} {rank.label}
              </span>
            </div>

            <div style={styles.metaRow}>
              <span style={styles.metaBubble}>📍 {profile.base_city}</span>
              <span style={styles.metaBubble}>💼 {profile.total_jobs} deals closed</span>
              <span style={{ 
                ...styles.metaBubble, 
                background: profile.is_available ? 'var(--sc-success-bg)' : 'var(--sc-error-bg)', 
                color: profile.is_available ? 'var(--sc-success-text)' : 'var(--sc-error-text)' 
              }}>
                {profile.is_available ? '🟢 Available' : '🔴 Unavailable'}
              </span>
            </div>

            <div style={styles.ratingRow}>
              <div style={styles.starsDisplay}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <span key={n} style={{ fontSize: 18, color: n <= fullStars ? 'var(--sc-warning)' : '#D1D5DB' }}>★</span>
                ))}
              </div>
              <span style={styles.ratingNum}>{avgRating.toFixed(1)}</span>
              <span style={styles.reviewCount}>({ratings.length} reviews)</span>
            </div>
          </div>
        </div>

        {/* Rating Breakdown Graph */}
        <div style={styles.ratingBreakdown}>
          <span style={styles.bigRating}>{avgRating.toFixed(1)}</span>
          <div style={styles.bigRatingBar}>
            <div style={{ ...styles.bigRatingFill, width: `${ratingPct}%` }} />
          </div>
          <span style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', fontWeight: 600 }}>OUT OF 5.0</span>
          
          <div style={styles.ratingBars}>
            {[5, 4, 3, 2, 1].map((star) => {
              const cnt = ratings.filter((r) => r.stars === star).length;
              const pct = ratings.length > 0 ? (cnt / ratings.length) * 100 : 0;
              return (
                <div key={star} style={styles.ratingBarRow}>
                  <span style={styles.ratingBarLabel}>{star}★</span>
                  <div style={styles.ratingBarTrack}>
                    <div style={{ ...styles.ratingBarFill, width: `${pct}%`, background: star >= 4 ? 'var(--sc-success)' : star === 3 ? 'var(--sc-warning)' : 'var(--sc-error)' }} />
                  </div>
                  <span style={styles.ratingBarCount}>{cnt}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <div style={styles.mainGrid}>
        <div style={styles.gridColumn}>
          {/* Services Offered */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>🛠️ Services Offered</h3>
            {myCategories.length === 0 ? (
              <p style={{ color: 'var(--sc-text-secondary)', fontSize: 13, margin: 0 }}>No service categories cataloged.</p>
            ) : (
              myCategories.map((cat) => {
                const catServices = profile.service_types.filter((st) => cat.services.includes(st));
                if (catServices.length === 0) return null;
                return (
                  <div key={cat.id} style={styles.catGroup}>
                    <div style={{ ...styles.catGroupHeader, color: cat.color }}>
                      <span style={{ marginRight: 6 }}>{cat.icon}</span>
                      {cat.name}
                    </div>
                    <div style={styles.serviceChipRow}>
                      {catServices.map((st) => (
                        <span key={st} style={{ ...styles.serviceChip, background: cat.color + '18', color: cat.color, borderColor: cat.color + '40' }}>
                          {st}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Performance Stats */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>📈 Performance History</h3>
            <div style={styles.statsGrid}>
              {[
                { label: 'Completed Jobs', value: profile.total_jobs, color: 'var(--sc-brand-primary)' },
                { label: 'Feedback Average', value: `${avgRating.toFixed(1)} ★`, color: 'var(--sc-warning)' },
                { label: 'Platform Ranking', value: rank.label, color: rank.color },
                { label: 'Total Reviews', value: ratings.length, color: 'var(--sc-success)' },
              ].map((stat) => (
                <div key={stat.label} style={styles.statCard}>
                  <div style={{ ...styles.statNumValue, color: stat.color }}>{stat.value}</div>
                  <div style={styles.statLabelText}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={styles.gridColumn}>
          {/* Review Proposal block */}
          {canReview && (
            <div style={{ ...styles.card, border: '1.5px solid var(--sc-success)' }}>
              <h3 style={styles.cardTitle}>✍️ Submit Surveyor Feedback</h3>
              <p style={{ fontSize: 13, color: 'var(--sc-text-secondary)', margin: '0 0 16px', lineHeight: 1.4 }}>
                Provide rating feedback based on your completed project deal with this surveyor.
              </p>
              
              {submitError && <div style={styles.errorBox}>{submitError}</div>}

              {/* Stars selector */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    style={styles.starBtn}
                    onMouseEnter={() => setHoverStar(n)}
                    onMouseLeave={() => setHoverStar(0)}
                    onClick={() => setReviewStars(n)}
                    type="button"
                  >
                    <span style={{ fontSize: 32, color: n <= (hoverStar || reviewStars) ? 'var(--sc-warning)' : 'var(--sc-border)', transition: 'color 0.1s' }}>★</span>
                  </button>
                ))}
              </div>
              
              {(reviewStars > 0 || hoverStar > 0) && (
                <div style={{ fontSize: 13, color: 'var(--sc-text-primary)', fontWeight: 700, marginBottom: 12 }}>
                  {starLabels[hoverStar || reviewStars]}
                </div>
              )}

              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Write a comment about service delivery, timeline compliance, and response times..."
                maxLength={500}
                rows={3}
                style={styles.textarea}
              />
              
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                <span style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', fontWeight: 600 }}>{reviewComment.length}/500</span>
                <button
                  onClick={handleSubmitReview}
                  disabled={reviewStars === 0 || submitting}
                  style={{ 
                    ...styles.submitReviewBtn, 
                    opacity: reviewStars === 0 || submitting ? 0.5 : 1,
                  }}
                  className="sc-press"
                >
                  {submitting ? 'Submitting...' : 'Submit Feedback'}
                </button>
              </div>
            </div>
          )}

          {submitted && (
            <div style={styles.successBox}>
              ✅ Review submitted successfully! Thank you for helping keep platform ratings trustworthy.
            </div>
          )}

          {alreadyReviewed && !submitted && (
            <div style={styles.reviewedNoticeBox}>
              ✓ Review log confirmed for this surveyor.
            </div>
          )}

          {/* Feedback list */}
          <div style={styles.card}>
            <h3 style={styles.cardTitle}>💬 Client Reviews ({ratings.length})</h3>
            {ratings.length === 0 ? (
              <div style={styles.emptyReviews}>
                <AnimatedIcon name="chat" size={32} color="var(--sc-text-tertiary)" />
                <p style={{ color: 'var(--sc-text-secondary)', fontSize: 13, margin: '8px 0 0' }}>No reviews recorded yet.</p>
              </div>
            ) : (
              <div style={styles.reviewsList}>
                {ratings.map((r) => (
                  <div key={r.id} style={styles.reviewCard}>
                    <div style={styles.reviewHeader}>
                      <div style={styles.reviewAvatar}>
                        {(r.from_user.first_name?.[0] || 'U').toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={styles.reviewerName}>{r.from_user.first_name} {r.from_user.last_name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                          <div style={{ display: 'flex' }}>
                            {[1, 2, 3, 4, 5].map((n) => (
                              <span key={n} style={{ fontSize: 13, color: n <= r.stars ? 'var(--sc-warning)' : 'var(--sc-border)' }}>★</span>
                            ))}
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--sc-text-tertiary)', fontWeight: 500 }}>
                            {new Date(r.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div style={{ 
                        ...styles.starsBadge, 
                        background: r.stars >= 4 ? 'var(--sc-success-bg)' : r.stars === 3 ? 'var(--sc-warning-bg)' : 'var(--sc-error-bg)', 
                        color: r.stars >= 4 ? 'var(--sc-success-text)' : r.stars === 3 ? 'var(--sc-warning-text)' : 'var(--sc-error-text)' 
                      }}>
                        {r.stars}.0
                      </div>
                    </div>
                    {r.comment && (
                      <p style={styles.reviewComment}>{r.comment}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  pageContainer: { maxWidth: 860, margin: '0 auto', paddingBottom: 40, fontFamily: 'var(--sc-font-family)' },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 320, gap: 12 },
  spinner: { width: 32, height: 32, border: '3px solid var(--sc-border)', borderTopColor: 'var(--sc-brand-primary)', borderRadius: '50%', animation: 'sc-spin 0.8s linear infinite' },
  backBtn: { display: 'inline-flex', alignItems: 'center', background: 'none', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-md)', padding: '8px 16px', fontSize: 13, fontWeight: 700, color: 'var(--sc-text-primary)', cursor: 'pointer', marginBottom: 18 },

  heroCard: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 24,
    background: 'var(--sc-bg-secondary)',
    borderRadius: 'var(--sc-radius-lg)',
    padding: 24,
    border: '1px solid var(--sc-border)',
    boxShadow: 'var(--sc-shadow-card)',
    marginBottom: 20,
    flexWrap: 'wrap',
  },
  heroLeft: { display: 'flex', alignItems: 'flex-start', gap: 18, flex: 1, minWidth: 280 },
  avatar: {
    width: 72, height: 72, borderRadius: 'var(--sc-radius-lg)',
    background: 'var(--sc-brand-gradient)',
    color: '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontWeight: 800, fontSize: 24,
    boxShadow: 'var(--sc-shadow-brand)',
  },
  verifiedBadgeOverlay: {
    position: 'absolute', bottom: -4, right: -4,
    width: 20, height: 20, borderRadius: '50%',
    background: 'var(--sc-success)', color: '#FFF',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 800,
    border: '2px solid var(--sc-bg-secondary)',
    boxShadow: 'var(--sc-shadow-sm)',
  },
  nameRow: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  name: { fontSize: 20, fontWeight: 800, color: 'var(--sc-text-primary)', margin: 0, fontFamily: 'var(--sc-font-display)' },
  verifiedTag: { padding: '3px 8px', borderRadius: 20, background: 'var(--sc-success-bg)', color: 'var(--sc-success-text)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' },
  rankTag: { padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 },
  metaRow: { display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0 10px' },
  metaBubble: { padding: '4px 10px', background: 'var(--sc-bg-input)', borderRadius: 20, fontSize: 12, color: 'var(--sc-text-secondary)', border: '1px solid var(--sc-border-light)' },

  ratingRow: { display: 'flex', alignItems: 'center', gap: 8 },
  starsDisplay: { display: 'flex', gap: 1 },
  ratingNum: { fontSize: 16, fontWeight: 800, color: 'var(--sc-text-primary)' },
  reviewCount: { fontSize: 12, color: 'var(--sc-text-tertiary)', fontWeight: 500 },

  ratingBreakdown: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    minWidth: 160, padding: '4px 0',
  },
  bigRating: { fontSize: 44, fontWeight: 900, color: 'var(--sc-text-primary)', lineHeight: 1 },
  bigRatingBar: { width: 120, height: 6, background: 'var(--sc-border)', borderRadius: 3, overflow: 'hidden', margin: '8px 0' },
  bigRatingFill: { height: '100%', background: 'var(--sc-warning)', borderRadius: 3 },
  ratingBars: { width: '100%', display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10 },
  ratingBarRow: { display: 'flex', alignItems: 'center', gap: 6 },
  ratingBarLabel: { width: 20, fontSize: 11, color: 'var(--sc-text-secondary)', textAlign: 'right' },
  ratingBarTrack: { flex: 1, height: 5, background: 'var(--sc-bg-input)', borderRadius: 2 },
  ratingBarFill: { height: '100%', borderRadius: 2, minWidth: 2 },
  ratingBarCount: { width: 16, fontSize: 11, color: 'var(--sc-text-tertiary)', textAlign: 'right' },

  mainGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 20 },
  gridColumn: { display: 'flex', flexDirection: 'column', gap: 20 },
  card: { background: 'var(--sc-bg-secondary)', borderRadius: 'var(--sc-radius-lg)', padding: 24, border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-card)' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: 'var(--sc-text-primary)', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--sc-font-display)' },

  catGroup: { marginBottom: 16 },
  catGroupHeader: { display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 700, marginBottom: 8 },
  serviceChipRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  serviceChip: { padding: '5px 11px', borderRadius: 'var(--sc-radius-sm)', border: '1px solid', fontSize: 12, fontWeight: 600 },

  statsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  statCard: { background: 'var(--sc-bg-input)', borderRadius: 'var(--sc-radius-md)', padding: 14, textAlign: 'center', border: '1px solid var(--sc-border-light)' },
  statNumValue: { fontSize: 20, fontWeight: 800, marginBottom: 4 },
  statLabelText: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 600 },

  starBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 2, lineHeight: 1 },
  textarea: { width: '100%', borderRadius: 'var(--sc-radius-md)', border: '1px solid var(--sc-border)', padding: '10px 12px', fontFamily: 'inherit', fontSize: 13, resize: 'vertical', outline: 'none', boxSizing: 'border-box', background: 'var(--sc-bg-input)', color: 'var(--sc-text-primary)' },
  submitReviewBtn: { height: 36, padding: '0 16px', background: 'var(--sc-success)', color: '#FFF', border: 'none', borderRadius: 'var(--sc-radius-md)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  errorBox: { background: 'var(--sc-error-bg)', border: '1px solid var(--sc-error)', borderRadius: 'var(--sc-radius-md)', padding: '10px 14px', fontSize: 13, color: 'var(--sc-error-text)', marginBottom: 12 },
  successBox: { background: 'var(--sc-success-bg)', border: '1px solid var(--sc-success)', borderRadius: 'var(--sc-radius-lg)', padding: '14px 18px', fontSize: 13, color: 'var(--sc-success-text)', fontWeight: 600 },
  reviewedNoticeBox: { background: 'var(--sc-bg-input)', border: '1px solid var(--sc-border)', borderRadius: 'var(--sc-radius-md)', padding: '12px 16px', fontSize: 13, color: 'var(--sc-text-secondary)', fontWeight: 600, textAlign: 'center' },

  emptyReviews: { textAlign: 'center', padding: '24px 0' },
  reviewsList: { display: 'flex', flexDirection: 'column', gap: 14 },
  reviewCard: { padding: '14px 16px', background: 'var(--sc-bg-input)', borderRadius: 'var(--sc-radius-md)', border: '1px solid var(--sc-border)', boxShadow: 'var(--sc-shadow-sm)' },
  reviewHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 },
  reviewAvatar: { width: 36, height: 36, borderRadius: '50%', background: 'var(--sc-brand-primary-bg)', color: 'var(--sc-brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 },
  reviewerName: { fontSize: 13.5, fontWeight: 700, color: 'var(--sc-text-primary)' },
  starsBadge: { padding: '3px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700 },
  reviewComment: { fontSize: 13, color: 'var(--sc-text-secondary)', lineHeight: 1.5, margin: '4px 0 0 48px' },
};
